import React from 'react';
import styles from './DataList.module.css';

const SearchIcon = (props) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" aria-hidden="true" {...props}>
        <circle cx="11" cy="11" r="7"/>
        <path d="M20 20l-3.5-3.5"/>
    </svg>
);

const SortIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
        <path d="M12 5v14M6 13l6 6 6-6"/>
    </svg>
);

// The white card the whole list sits in.
export const DataListCard = ({children}) => (
    <div className={styles.card}>{children}</div>
);

// Search + segmented filter + primary action.
export const DataListToolbar = ({
    searchValue, onSearchChange, searchPlaceholder = 'Search',
    filters, activeFilter, onFilterChange, filterLabel = 'Filter',
    primaryAction,
}) => (
    <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
            <label className={styles.searchWrap}>
                <SearchIcon className={styles.searchIcon}/>
                <input
                    type="search"
                    value={searchValue}
                    onChange={(e) => onSearchChange(e.target.value)}
                    placeholder={searchPlaceholder}
                    aria-label={searchPlaceholder}
                    className={styles.searchInput}
                />
            </label>
            {filters && (
                <div role="group" aria-label={filterLabel} className={styles.filterGroup}>
                    {filters.map((f) => (
                        <button
                            key={f.value}
                            type="button"
                            aria-pressed={activeFilter === f.value}
                            className={styles.filterButton}
                            onClick={() => onFilterChange(f.value)}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
        {primaryAction}
    </div>
);

// Header row: select-all checkbox, column labels, a sortable "Updated" button.
export const DataListHeaderRow = ({
    selectable = true, allSelected, onSelectAllChange,
    sortDirection, onSortChange,
}) => (
    <div role="row" className={styles.headerRow}>
        <span role="columnheader" className={styles.headerCheck}>
            {selectable && (
                <input
                    type="checkbox"
                    checked={!!allSelected}
                    onChange={(e) => onSelectAllChange(e.target.checked)}
                    aria-label="Select all pages"
                    className={styles.headerCheckbox}
                />
            )}
        </span>
        <span role="columnheader" className={styles.headerLabel}>Page</span>
        <span role="columnheader" className={styles.headerLabel}>Progress</span>
        <span role="columnheader" className={styles.headerLabel}>Owner</span>
        <span role="columnheader" aria-sort={sortDirection === 'asc' ? 'ascending' : 'descending'}>
            <button
                type="button"
                aria-label={`Sort by last updated, ${sortDirection === 'desc' ? 'newest' : 'oldest'} first`}
                className={styles.sortButton}
                onClick={onSortChange}
            >
                Updated<SortIcon style={{transform: sortDirection === 'asc' ? 'rotate(180deg)' : undefined}}/>
            </button>
        </span>
        <span role="columnheader" className={styles.headerActions}>
            <span className={styles.srOnly}>Actions</span>
        </span>
    </div>
);

// 72px shimmering placeholder rows, shown while the list is loading - always
// before an empty/error/no-results state, so neither flashes on first load.
export const DataListSkeleton = ({rows = 5}) => (
    <div aria-hidden="true">
        {Array.from({length: rows}).map((_, i) => (
            <div key={i} className={styles.skeletonRow}>
                <span className={`${styles.shimmer} ${styles.skeletonThumb}`}/>
                <span className={`${styles.shimmer} ${styles.skeletonLine}`}/>
            </div>
        ))}
    </div>
);
